import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, ShieldCheck, Truck, TrendingUp, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, stats, products, whyChooseUs } from '../data/mock';
import ProductCard from '../components/ProductCard';

const Home = () => {
  const featuredProducts = products.slice(0, 8);

  return (
    <main>
      <section className="relative pt-28 pb-24 lg:pt-32 lg:pb-32 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(148,163,184,0.16),_transparent_30%)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900/90 opacity-95 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-slate-700 rounded-full bg-slate-900/80 text-slate-300">
                <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                <span className="text-sm font-medium">Trusted by 1000+ businesses worldwide</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight leading-tight">
                Pinto Trading
                <span className="block text-slate-300 mt-3 text-3xl md:text-4xl">Your Trusted Global<br /> Trading Partner.</span>
              </h1>

              <p className="max-w-2xl text-lg text-slate-300 leading-relaxed">
                {companyInfo.description} Our team delivers tailored supply chain solutions, quality control, and efficient logistics for companies seeking reliable international trading partners.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-slate-950 hover:bg-slate-100 px-8 gap-2">
                    Request a Quote
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button size="lg" variant="outline" className="border-slate-500 text-slate-200 hover:border-white hover:text-white px-8">
                    View Product Catalog
                  </Button>
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-5">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className={`rounded-3xl p-6 border border-slate-800 bg-slate-900/95 text-white shadow-lg transition-transform duration-300 ${
                    index === 0 ? 'lg:translate-y-8' : index === 3 ? 'lg:-translate-y-8' : ''
                  }`}
                >
                  <p className="text-3xl md:text-4xl font-semibold mb-2">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.3em] text-slate-500 mb-4">Why Choose Pinto</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-4">A trusted partner for global trade operations</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We combine market intelligence, quality assurance, and logistics expertise to help businesses scale with confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="rounded-3xl border border-slate-200 p-6 shadow-sm hover:shadow-lg transition-shadow duration-300 bg-white">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-slate-900 text-white mb-5">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-slate-500 mb-3">Featured Products</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">Quality products for global business</h2>
              <p className="mt-4 text-slate-600 max-w-2xl">Our catalog highlights reliable, high-demand products selected for international wholesale buyers.</p>
            </div>
            <Link to="/products" className="shrink-0">
              <Button variant="outline" className="gap-2 px-6 py-3 border-slate-300 text-slate-700 hover:border-slate-500 hover:text-slate-900">
                View All Products
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.35em] text-slate-500 mb-4">Corporate Services</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">Comprehensive trading capabilities</h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                From sourcing to delivery, we provide end-to-end trading services that help businesses grow and succeed in the global marketplace.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Globe, text: 'Global Network in 50+ Countries' },
                  { icon: ShieldCheck, text: 'Quality Assurance Guaranteed' },
                  { icon: Truck, text: 'Fast & Reliable Logistics' },
                  { icon: TrendingUp, text: 'Competitive Market Pricing' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-11 h-11 mt-1 rounded-xl bg-slate-950 text-white flex items-center justify-center shadow-sm">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/services" className="inline-block mt-10">
                <Button className="bg-slate-950 text-white hover:bg-slate-800 gap-2 px-8 py-3">
                  Explore Services
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 text-white shadow-2xl">
              <div className="p-10 sm:p-12">
                <span className="inline-flex items-center justify-center rounded-full bg-emerald-400/10 text-emerald-300 px-4 py-2 text-xs uppercase tracking-[0.35em] font-semibold mb-6">
                  Trusted Partner
                </span>
                <h3 className="text-3xl font-semibold mb-4">Ready to scale your supply chain?</h3>
                <p className="text-slate-300 mb-8 leading-relaxed">
                  Join thousands of businesses that trust Pinto Trading for consistent, quality sourcing and logistics support.
                </p>
                <Link to="/contact">
                  <Button className="bg-white text-slate-950 hover:bg-slate-100 gap-2 px-8 py-3">
                    Get Started Today
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
              <div className="absolute -bottom-6 -right-6 w-44 h-44 rounded-full bg-white/5 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-[0.35em] text-emerald-300 mb-4">Let’s partner</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
            Start growing your business with a dependable global trading partner.
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Contact us to discuss your trading requirements. Our team is ready to help you source quality products at competitive prices.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-slate-100 text-slate-950 px-8 py-3">
                Request a Quote
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800 px-8 py-3">
                View Catalog
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
